import type { Direction, DisruptionLink, Line } from '../arcticapi/types';
import { buildLineDisruptionLink } from '../components/base/DisruptionAlert/disruptionLinks';

export interface TimetableDetailsDirection {
  name: string;
  origin?: string;
  destination?: string;
}

export interface TimetableDetailsLine {
  id: string;
  name: string;
  title?: string;
  description?: string;
  disruption: DisruptionLink | null;
  via?: string | null;
  operatorName?: string | null;
  colors: Line['colors'];
  timetableHref: string | null;
  assetsHref?: string | null;
  shapeHref: string | null;
  stopsHref: string | null;
  directions?: TimetableDetailsDirection[];
  label?: string;
}

interface CreateTimetableDetailsLineOptions {
  id?: string;
  name: string;
  title?: string;
  description?: string;
  disruption?: DisruptionLink | null;
  colors?: Line['colors'];
  timetableHref?: string | null;
  assetsHref?: string | null;
  shapeHref?: string | null;
  stopsHref?: string | null;
  directions?: TimetableDetailsDirection[];
}

function mapDirections(
  directions?: Direction[],
): TimetableDetailsDirection[] | undefined {
  const mappedDirections = directions
    ?.map((direction) => ({
      name: direction.name,
      origin: direction.origin,
      destination: direction.destination,
    }))
    .filter((direction) => direction.name);

  return mappedDirections?.length ? mappedDirections : undefined;
}

/**
 * Builds the smallest details-line model needed by timetable detail pages.
 *
 * Callers can provide only the data they actually have; unavailable assets and
 * map links are represented as null and handled lower in the timetable flow.
 */
export function createTimetableDetailsLine(
  options: CreateTimetableDetailsLineOptions,
): TimetableDetailsLine {
  return {
    id: options.id ?? options.timetableHref ?? options.name,
    name: options.name,
    title: options.title,
    description: options.description,
    disruption: options.disruption ?? null,
    via: null,
    operatorName: null,
    colors: options.colors,
    timetableHref: options.timetableHref ?? null,
    assetsHref: options.assetsHref ?? null,
    shapeHref: options.shapeHref ?? null,
    stopsHref: options.stopsHref ?? null,
    directions: options.directions,
    label: options.name,
  };
}

export function mapLineToTimetableDetailsLine(
  line: Line,
  websiteBaseHref: string | null = null,
): TimetableDetailsLine {
  return {
    id: line.id,
    name: line.name,
    title: line.title ?? undefined,
    description: line.description ?? line.title ?? undefined,
    disruption: buildLineDisruptionLink(
      websiteBaseHref,
      {
        lineId: line.id,
        operatorCode: line.operator?.code ?? line.links?.timetable?.operator,
        lineName: line.name,
        href:
          line.links?.self ?? line.links?.alerts ?? line.links?.timetable?.href,
      },
      line.alerts?.length ?? 0,
    ),
    via: line.directions?.[0]?.vias?.[0] ?? null,
    operatorName: line.operator?.name ?? null,
    colors: line.colors,
    timetableHref: line.links?.timetable?.href ?? null,
    assetsHref: line.links?.assets ?? null,
    shapeHref: line.links?.shapes ?? null,
    stopsHref: line.links?.stops ?? null,
    directions: mapDirections(line.directions),
    label: `${line.name}${line.title ? ` - ${line.title}` : ''}${line.operator?.name ? ` (${line.operator.name})` : ''}`,
  };
}
